import {produce} from 'immer';
import {assign, setup, fromPromise, assertEvent} from 'xstate';

import {
  clearProteinCreationSettings,
  getProteinCreationSettings,
  ProteinCreationSettingsData,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationSettingsUtil';
import {
  buildProteinCreationTableRequest,
  BuildProteinCreationTableRequestInput,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationSubmissionUtil';
import {ProteinCreationRow} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTypes';
import {
  getSubmissionValidationErrors,
  guardRelatedSchemasConfigIsValid,
  guardSelectedProjectFolderIsValid,
  hasSubmissionValidationErrors,
} from 'antibodies/nested-data-creation/ProteinCreationSharedUtil';
import * as AntibodyNestedDataCreationSpreadsheetImportDAO from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportDAO';
import {
  InvalidProteinSchemaError,
  QueryAntibodyComponentAndRelatedSchemasInput,
} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportDAO';
import {
  AntibodyComponentAndRelatedSchemas,
  INITIAL_ANTIBODY_NDC_TRANSLATION_PARAMETERS,
  INITIAL_RELATED_SCHEMAS_CONFIG,
  NdcRelatedSchemasConfig,
  NdcSpreadsheetImportSubmissionValidationErrors,
  SchemaFetchErrorKind,
  TranslationParameters,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {assert} from 'Asserts';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import notifications from 'notifications';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as logging from 'util/Logging';

export interface ProteinCreationValidationErrors {
  rowErrors: Array<{rowIndex: number; errorMessage: string}>;
  batchErrors: Array<string>;
}

export interface ProteinCreationFlowContext {
  creationSettings: ProteinCreationSettingsData;
  relatedComponentSchemas: {
    available: AntibodyComponentAndRelatedSchemas | null;
    selected: NdcRelatedSchemasConfig;
    errorKind: SchemaFetchErrorKind | null;
  };
  projectFolderId: string | null;
  translationParameters: TranslationParameters;
  selectedDocumentId: string | null;
  validationErrors: ProteinCreationValidationErrors | null;
  ndcWorkspaceUrl: string | null;
}

type SubmitProteinCreationOutput =
  | {ndcWorkspaceUrl: string | null}
  | {submissionValidationErrors: NdcSpreadsheetImportSubmissionValidationErrors};

export type ProteinCreationFlowEvents =
  | {type: 'next'; hasData: boolean; hasBlockingErrors: boolean}
  | {type: 'setProjectFolder'; projectFolderId: string}
  | {type: 'setRelatedSchemasConfig'; relatedSchemasConfig: NdcRelatedSchemasConfig}
  | {type: 'setTranslationParameters'; translationParameters: TranslationParameters}
  | {type: 'setSelectedDocumentId'; documentId: string | null}
  | {type: 'submit'; rows: ReadonlyArray<ProteinCreationRow>; hasDnaInputs: boolean}
  | {type: 'back'}
  | {type: 'backToEditing'}
  | {type: 'retry'}
  /** Replaces settings that don't shape the table, keeping all flow state. */
  | {type: 'updateCreationSettings'; creationSettings: ProteinCreationSettingsData}
  /** Replaces settings that shape the table, resetting the state derived from them. */
  | {type: 'resetCreationSettings'; creationSettings: ProteinCreationSettingsData};

const ProteinCreationFlowMachine = setup({
  types: {
    context: {} as ProteinCreationFlowContext,
    events: {} as ProteinCreationFlowEvents,
    input: {},
  },
  actors: {
    fetchComponentAndRelatedSchemas: fromPromise(
      async ({
        input,
      }: {
        input: QueryAntibodyComponentAndRelatedSchemasInput;
      }): Promise<AntibodyComponentAndRelatedSchemas> =>
        await AntibodyNestedDataCreationSpreadsheetImportDAO.queryAntibodyComponentAndRelatedSchemas(input)
    ),
    submitProteinCreation: fromPromise(
      async ({
        input,
      }: {
        input: BuildProteinCreationTableRequestInput;
      }): Promise<SubmitProteinCreationOutput> => {
        const request = buildProteinCreationTableRequest(input);
        try {
          const result =
            await AntibodyNestedDataCreationSpreadsheetImportDAO.createProteinsFromTable(request);
          return {ndcWorkspaceUrl: result.ndcWorkspaceUrl ?? null};
        } catch (e) {
          if (e.status === 400) {
            const submissionValidationErrors = getSubmissionValidationErrors(e);
            if (submissionValidationErrors != null) {
              return {submissionValidationErrors};
            }
          }
          logging.error(e, {extra: {message: 'Failed to create proteins from table'}});
          notifications.notifyError(e);
          throw e;
        }
      }
    ),
  },
  actions: {
    setProjectFolder: assign(({context, event}) => {
      assertEvent(event, 'setProjectFolder');
      return produce(context, (draft) => {
        draft.projectFolderId = event.projectFolderId;
      });
    }),
    setRelatedSchemasConfig: assign(({context, event}) => {
      assertEvent(event, 'setRelatedSchemasConfig');
      return produce(context, (draft) => {
        draft.relatedComponentSchemas.selected = event.relatedSchemasConfig;
      });
    }),
    setTranslationParameters: assign(({context, event}) => {
      assertEvent(event, 'setTranslationParameters');
      return produce(context, (draft) => {
        draft.translationParameters = event.translationParameters;
      });
    }),
    setSelectedDocumentId: assign(({context, event}) => {
      assertEvent(event, 'setSelectedDocumentId');
      return produce(context, (draft) => {
        draft.selectedDocumentId = event.documentId;
      });
    }),
    updateCreationSettings: assign(({context, event}) => {
      assertEvent(event, 'updateCreationSettings');
      return produce(context, (draft) => {
        draft.creationSettings = event.creationSettings;
      });
    }),
    reset: assign(({context, event}) => {
      assertEvent(event, 'resetCreationSettings');
      return produce(context, (draft) => {
        draft.creationSettings = event.creationSettings;
        draft.relatedComponentSchemas = {
          available: null,
          selected: INITIAL_RELATED_SCHEMAS_CONFIG,
          errorKind: null,
        };
        draft.validationErrors = null;
      });
    }),
  },
  guards: {
    guardCanAdvanceToFinalize: ({event}) => {
      assertEvent(event, 'next');
      return event.hasData && !event.hasBlockingErrors;
    },
  },
}).createMachine({
  context: ({input}) => {
    const settings = getProteinCreationSettings();
    if (settings == null) {
      throw new Error('Protein creation settings must be set before starting the flow');
    }
    clearProteinCreationSettings();
    return {
      creationSettings: settings,
      relatedComponentSchemas: {
        available: null,
        selected: INITIAL_RELATED_SCHEMAS_CONFIG,
        errorKind: null,
      },
      projectFolderId:
        FolderDefaults.get(FolderDefaultsKey.ANTIBODY) ??
        FolderDefaults.get(FolderDefaultsKey.INVENTORY) ??
        null,
      translationParameters: INITIAL_ANTIBODY_NDC_TRANSLATION_PARAMETERS,
      selectedDocumentId: null,
      validationErrors: null,
      ndcWorkspaceUrl: null,
    };
  },
  id: 'proteinCreationFlow',
  initial: 'initializing',
  states: {
    initializing: {
      invoke: {
        src: 'fetchComponentAndRelatedSchemas',
        input: ({context}): QueryAntibodyComponentAndRelatedSchemasInput => ({
          registryId: context.creationSettings.selectedRegistryId,
          proteinSchemaId: context.creationSettings.selectedSchemaId,
        }),
        onDone: {
          target: 'editing',
          actions: assign(({context, event}) =>
            produce(context, (draft) => {
              draft.relatedComponentSchemas.available = event.output;
              draft.relatedComponentSchemas.errorKind = null;
            })
          ),
        },
        onError: {
          actions: assign(({context, event}) => {
            const error = event.error;
            const errorKind: SchemaFetchErrorKind =
              error instanceof InvalidProteinSchemaError ? 'invalidSchema' : 'fetchFailed';
            // Logged even though it is user-recoverable: a selected schema failing validation is unexpected.
            logging.error(error as Error, {
              extra: {
                message:
                  errorKind === 'invalidSchema'
                    ? 'Selected protein schema failed validation'
                    : 'Failed to fetch component and related schemas',
                selectedSchemaId: context.creationSettings.selectedSchemaId,
                selectedRegistryId: context.creationSettings.selectedRegistryId,
              },
            });
            return produce(context, (draft) => {
              draft.relatedComponentSchemas.available = null;
              draft.relatedComponentSchemas.errorKind = errorKind;
            });
          }),
        },
      },
      on: {
        retry: {
          target: 'initializing',
          reenter: true,
          // Clear the prior failure so the loader shows during the refetch instead of the stale alert.
          actions: assign(({context}) =>
            produce(context, (draft) => {
              draft.relatedComponentSchemas.errorKind = null;
            })
          ),
        },
      },
    },
    editing: {
      on: {
        next: {
          target: 'finalize',
          guard: 'guardCanAdvanceToFinalize',
        },
        updateCreationSettings: {actions: 'updateCreationSettings'},
        resetCreationSettings: {
          target: 'initializing',
          actions: 'reset',
        },
      },
    },
    finalize: {
      on: {
        setProjectFolder: {actions: 'setProjectFolder'},
        setRelatedSchemasConfig: {actions: 'setRelatedSchemasConfig'},
        setTranslationParameters: {actions: 'setTranslationParameters'},
        setSelectedDocumentId: {actions: 'setSelectedDocumentId'},
        submit: {
          target: 'submitting',
          guard: ({context, event}) => {
            assertEvent(event, 'submit');
            return (
              guardRelatedSchemasConfigIsValid(
                context.relatedComponentSchemas.selected,
                event.hasDnaInputs
              ) && guardSelectedProjectFolderIsValid(context.projectFolderId)
            );
          },
          actions: assign(({context}) =>
            produce(context, (draft) => {
              draft.validationErrors = null;
            })
          ),
        },
        back: {
          target: 'editing',
          actions: assign(({context}) =>
            produce(context, (draft) => {
              draft.validationErrors = null;
            })
          ),
        },
      },
    },
    submitting: {
      entry: () => {
        LoadingScreen.show('Creating proteins...');
      },
      exit: () => {
        LoadingScreen.hide();
      },
      invoke: {
        src: 'submitProteinCreation',
        input: ({context, event}): BuildProteinCreationTableRequestInput => {
          assertEvent(event, 'submit');
          return {
            rows: event.rows,
            format: context.creationSettings.selectedFormat,
            startingType: context.creationSettings.selectedStartingType,
            proteinSchemaId: context.creationSettings.selectedSchemaId,
            folderId: context.projectFolderId!,
            relatedSchemasConfig: context.relatedComponentSchemas.selected,
            translationParameters: context.translationParameters,
            germlineConfig: context.creationSettings.germlineConfig,
            documentId: context.selectedDocumentId,
            namingStrategy: context.creationSettings.namingStrategy,
          };
        },
        onDone: [
          {
            target: 'validationErrors',
            guard: ({event}) =>
              'submissionValidationErrors' in event.output &&
              hasSubmissionValidationErrors(event.output.submissionValidationErrors),
            actions: assign(({context, event}) => {
              assert('submissionValidationErrors' in event.output, 'Expected submission validation errors');
              const {validationErrors, batchErrors} = event.output.submissionValidationErrors;
              return produce(context, (draft) => {
                draft.validationErrors = {rowErrors: validationErrors, batchErrors};
              });
            }),
          },
          {
            target: 'done',
            actions: assign(({context, event}) =>
              produce(context, (draft) => {
                draft.ndcWorkspaceUrl =
                  'ndcWorkspaceUrl' in event.output ? event.output.ndcWorkspaceUrl : null;
              })
            ),
          },
        ],
        onError: {
          target: 'finalize',
        },
      },
    },
    validationErrors: {
      on: {
        backToEditing: {
          target: 'editing',
          actions: assign(({context}) =>
            produce(context, (draft) => {
              draft.validationErrors = null;
            })
          ),
        },
      },
    },
    done: {
      type: 'final',
      entry: ({context}) => {
        if (context.projectFolderId) {
          FolderDefaults.set(FolderDefaultsKey.ANTIBODY, context.projectFolderId);
        }
      },
    },
  },
});

export default ProteinCreationFlowMachine;
